import React, { useMemo } from "react";
import { type ModelFilterDispatchType, useModelFilterSelector } from "../../../redux/store";
import FilterDropdown from "./FilterDropdown";
import * as Styles from "./styles/FilterListStyles";
import CarTypeFilter from "./car-type/CarTypeFilter";
import FilterWrapper from "./FilterWrapper";
import { trackOpenFilter } from "../../../utils/tracking";
import { ModelFilterId } from "../../../utils/constants/filterConstants";
import { isCarType } from "../../../utils/filters";
import { setActiveFilters } from "../../../redux/actions/ModelFiltersActions";
import { ModelFilterLabel } from "../../../utils/labels";
import { SkipLink } from "../../../../../../common-deprecated/styles/v2/lexus/utilities/ScreenreaderOnly";
import { useDispatch } from "../../../../../../shared-logic/redux/store";
import { SKIP_ID_RESULTS } from "../../../utils/constants/constants";

const FilterList = (): JSX.Element => {
    const dispatch = useDispatch<ModelFilterDispatchType>();
    const modelFilters = useModelFilterSelector((state) => state.modelFilters);
    const activeFilterIds = modelFilters.activeFilters;
    const carTypeFilterId = ModelFilterId.CarType;

    // Calculate and sort the filters that should be rendered.
    // Car Type is always excluded from this list as it is always rendered first and has custom render logic.
    const activeFilters = Object.values(ModelFilterId)
        .filter((filterId) => modelFilters[filterId].show && !isCarType(filterId))
        .sort((a, b) => modelFilters[a].index - modelFilters[b].index);

    // Key is used as a memo argument to determine if the component needs rerendering.
    const activeFiltersKey = Object.keys(activeFilters).sort().join();

    // Add or remove the filter from the active filter state on click.
    const dropdownClick = (filterId: ModelFilterId) => () => {
        const isActive = activeFilterIds.includes(filterId);
        if (isActive) {
            dispatch(setActiveFilters(activeFilterIds.filter((id) => id !== filterId)));
        } else {
            trackOpenFilter(modelFilters[filterId].label, true);
            dispatch(setActiveFilters(activeFilterIds.concat([filterId])));
        }
    };

    return useMemo(() => {
        return (
            <Styles.Wrapper role="list">
                <SkipLink href={`#${SKIP_ID_RESULTS}`}>
                    <ModelFilterLabel label="modelfilterA11yButtonGoToResults" />
                </SkipLink>
                {modelFilters.modelType.show && (
                    <FilterDropdown
                        show={activeFilterIds.includes(carTypeFilterId)}
                        onClick={dropdownClick(carTypeFilterId)}
                        title={modelFilters[carTypeFilterId].label}
                    >
                        <CarTypeFilter />
                    </FilterDropdown>
                )}

                {activeFilters.map((filterId) => (
                    <FilterDropdown
                        show={activeFilterIds.includes(filterId)}
                        onClick={dropdownClick(filterId)}
                        title={modelFilters[filterId].label}
                        key={filterId}
                    >
                        <FilterWrapper filterId={filterId} />
                    </FilterDropdown>
                ))}
            </Styles.Wrapper>
        );
    }, [activeFiltersKey, activeFilterIds, carTypeFilterId, dropdownClick, modelFilters, activeFilters]);
};

export default FilterList;
